import type { ComputedRef } from 'vue'
import { computed } from 'vue'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import AddDeliverableModalContent from '@/features/sessions/components/add-deliverable-modal/add-deliverable-modal-content/add-deliverable-modal-content.vue'
import deliverablesRatingModalContent from '@/features/sessions/components/review-session-modal/deliverables-rating-modal-content/deliverables-rating-modal-content.vue'
import FinishedReviewModalContent from '@/features/sessions/components/review-session-modal/finished-review-modal-content/finished-review-modal-content.vue'
import MenteeReportModalContent from '@/features/sessions/components/review-session-modal/mentee-report-modal-content/mentee-report-modal-content.vue'
import PresencesSessionModalContent from '@/features/sessions/components/review-session-modal/presences-session-modal-content/presences-session-modal-content.vue'
import RateSessionModalContent from '@/features/sessions/components/review-session-modal/rate-session-modal-content/rate-session-modal-content.vue'
import ReportModalContent from '@/features/sessions/components/review-session-modal/report-modal-content/report-modal-content.vue'
import useDeliverables from '@/features/sessions/hooks/use-deliverables.hook'
import useSession from '@/features/sessions/hooks/use-session.hook'
import { selectGetSessionDetailsModal } from '@/features/ui/store/modal/modal.selectors'
import type { ReviewSessionModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useGroupRoles from '@/features/users/hooks/use-group-roles.hook'
import { useI18n } from '@/lib/i18n'
import { ANALYTICS_EVENTS, trackEvent } from '@/lib/posthog'
import type { ComponentFactoryType } from '@/types'
import { DO_NOT_ASK_REVIEW_AGAIN } from '@/utils/config/constants'

export function useSessionInReview() {
  const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
  const { programRef: selectedProgramRef } = useProgram(selectedProgramIdRef)

  const sessionDetailsModalRef = selectGetSessionDetailsModal(modalsStore)
  const sessionIdRef = computed(() => sessionDetailsModalRef.value.attachedData.sessionId ?? null)
  const { sessionRef, isPendingRef: isSessionPendingRef } = useSession(sessionIdRef)
  const { deliverablesRef } = useDeliverables(sessionIdRef)

  const { accountRef } = useAccount()
  const { t } = useI18n()
  const groupIdRef = computed(() => sessionRef.value?.mentoringGroupId)
  const { groupRolesRef } = useGroupRoles({ groupIdRef })

  // list of actions that can be done on the session
  const actionsListRef = computed(() => {
    if (!sessionRef.value) {
      return []
    }

    if (groupRolesRef.value?.includes(RoleEnumTypeV2.MENTOR)) {
      return [
        {
          title: t('session.pendingActions.review.title'),
          label: t('session.pendingActions.review.label'),
          // if the user has already rated the session, the status will be 'done'
          status: sessionRef.value.reviews.find(
            (review) => review.userId === accountRef.value?.id && typeof review.grade === 'number',
          )
            ? 'done'
            : 'pending',
          id: 'rating',
          component: RateSessionModalContent,
          isActiveOnThisProgram: selectedProgramRef.value?.settings.hasReviewSessionRating,
        },
        {
          title: t('session.pendingActions.presences.title'),
          label: t('session.pendingActions.presences.label'),
          // if the user has already filled the presence, the status will be 'done'
          status: sessionRef.value.presences.length > 0 ? 'done' : 'pending',
          id: 'presence',
          component: PresencesSessionModalContent,
          isActiveOnThisProgram: selectedProgramRef.value?.settings.hasMentorReviewSessionPresence,
        },
        {
          title: t('session.pendingActions.deliverables.title'),
          label: t('session.pendingActions.deliverables.label'),
          // if the user has already rated the deliverables, the status will be 'done'
          // if there is was no mentees present, the status will be 'done'
          status: sessionRef.value.deliverables.some((deliverable) => typeof deliverable.rating === 'number')
            ? // even if mentee wasn't present, we still want to display the deliverable rate
              // ||
              // (sessionRef.value.presences.length > 0 &&
              // sessionRef.value.presences
              // .filter((presence) => mentees.some((mentee) => mentee.id === presence.userId))
              // .every((presence) => !presence.wasPresent))

              'done'
            : 'pending',
          id: 'deliverables-rating',
          component: deliverablesRatingModalContent,
          isActiveOnThisProgram: selectedProgramRef.value?.settings.hasMentorReviewSessionDeliverableRate,
        },
        {
          title: t('session.pendingActions.report.title'),
          label: t('session.pendingActions.report.label'),
          // if the user has alredy sent the report, the status will be 'done'
          status: sessionRef.value.report ? 'done' : 'pending',
          id: 'report',
          component: ReportModalContent,
          isActiveOnThisProgram: selectedProgramRef.value?.settings.hasMentorReviewSessionDeliverableReport,
        },
        {
          title: t('session.pendingActions.finished.title'),
          label: t('session.pendingActions.finished.label'),
          status: 'pending',
          id: 'finished',
          component: FinishedReviewModalContent,
          isActiveOnThisProgram: true,
          isStepOnly: true, // will just act as a final step (not a pending action)
        },
      ].filter((action) => action.isActiveOnThisProgram)
    }
    if (groupRolesRef.value?.includes(RoleEnumTypeV2.MENTEE)) {
      return [
        {
          title: t('session.pendingActions.review.title'),
          label: t('session.pendingActions.review.label'),
          // if the user has already rated the session, the status will be 'done'
          status: sessionRef.value.reviews.find(
            (review) => review.userId === accountRef.value?.id && typeof review.grade === 'number',
          )
            ? 'done'
            : 'pending',
          id: 'rating',
          component: RateSessionModalContent,
          isActiveOnThisProgram: selectedProgramRef.value?.settings.hasReviewSessionRating,
        },
        {
          title: t('session.pendingActions.sendDeliverable.title'),
          label: t('session.pendingActions.sendDeliverable.label'),
          // if the user has already sent a deliverable or skipped it, the status will be 'done'
          status: (() => {
            const userDeliverable = deliverablesRef.value?.find(
              (deliverable) => deliverable.userId === accountRef.value?.id,
            )
            if (!userDeliverable) return 'pending'
            // If skipDeliverable is true, the step is considered validated
            if (userDeliverable.skipDeliverable === true) return 'done'
            // Otherwise, check if a file has been imported
            return userDeliverable.folder?.files.find((file) => file.isImported) ? 'done' : 'pending'
          })(),
          id: 'deliverable',
          component: AddDeliverableModalContent,
          isActiveOnThisProgram: selectedProgramRef.value?.settings.hasReviewSessionDeliverable,
        },
        {
          title: t('session.pendingActions.menteeReport.title'),
          label: t('session.pendingActions.menteeReport.label'),
          status: (() => {
            const currentUserReport = sessionRef.value.menteeReports?.find(
              (report) => report.userId === accountRef.value?.id
            )
            return currentUserReport && currentUserReport.comment ? 'done' : 'pending'
          })(),
          id: 'mentee-report',
          component: MenteeReportModalContent,
          isActiveOnThisProgram: selectedProgramRef.value?.settings.hasMenteeReviewSessionReport,
        },
        {
          title: t('session.pendingActions.finished.title'),
          label: t('session.pendingActions.finished.label'),
          status: 'pending',
          id: 'finished',
          component: FinishedReviewModalContent,
          isActiveOnThisProgram: true,
          isStepOnly: true, // will just act as a final step (not a pending action)
        },
      ].filter((action) => action.isActiveOnThisProgram)
    }

    // default case
    return []
  }) as ComputedRef<
    {
      title: string
      label: string
      status: 'done' | 'pending'
      id: ReviewSessionModalType['activeStep']
      component: ComponentFactoryType
      isActiveOnThisProgram: boolean | undefined
      isStepOnly?: boolean
    }[]
  >

  // filter the actions that are not just steps
  const pendingActionsRef = computed(() => actionsListRef.value.filter((action) => !action.isStepOnly))

  const currentPendingActionRef = computed(() => actionsListRef.value.find((action) => action.status === 'pending'))
  const nextPendingActionRef = computed(() => actionsListRef.value.find((action) => action.status === 'pending'))

  // mapping the actions to the steps structure
  const reviewStepsRef = computed(() =>
    actionsListRef.value.map((action) => ({ id: action.id, stepName: action.label, component: action.component })),
  )

  // function to call for every step
  function onClickNextStep() {
    if (!nextPendingActionRef.value) return

    modalsStore.setModalStep<ReviewSessionModalType>('review-session', nextPendingActionRef.value.id)
  }

  // function to call when clicking "complete session"
  function onClickCompletePendingActions() {
    console.log('onClickCompletePendingActions')
    if (!nextPendingActionRef.value) return

    console.log(nextPendingActionRef.value.id)

    // opening the review session modal
    modalsStore.toggleModal('session-details', false)
    modalsStore.toggleModal('review-session', true)

    modalsStore.updateModalAttachedData<ReviewSessionModalType>('review-session', {
      sessionId: sessionIdRef.value!,
    })

    // setting the next step
    onClickNextStep()
  }

  // function to call when clicking "fill later"
  function onCloseAndFillLater(cb?: () => void) {
    trackEvent(ANALYTICS_EVENTS.USER_CLICK_SESSION_REVIEW_FILL_LATER, {
      step: currentPendingActionRef.value?.id,
    })
    modalsStore.toggleModal('review-session', false)

    sessionStorage.setItem(DO_NOT_ASK_REVIEW_AGAIN, 'true')

    // additional callback to call after closing the modal
    if (cb && typeof cb === 'function') {
      cb()
    }
  }

  return {
    reviewStepsRef,
    pendingActionsRef,
    isSessionPendingRef,
    nextPendingActionRef,
    onClickCompletePendingActions,
    onClickNextStep,
    onCloseAndFillLater,
  }
}
